import BottomSheet from '@components/BottomSheet'
import TokenAvatar from '@components/TokenAvatar'
import CustomTokenForm from '@modules/Tokens/Components/CustomTokenForm'
import { useDevMode } from '@plug-core/state'
import { Button, Dialog, Heading, Input, ListGroup, ListRow } from '@ui'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

const NetworkForm = () => {
  const [kindInEdit, setKindInEdit] = useState<string>()
  const [newHostUrl, setNewHostUrl] = useState<string>()
  const [name, setName] = useState<string>()
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false)

  const { networkId } = useParams<{ networkId: string }>()

  const { t } = useTranslation()
  const { addNetwork, networks, networkTokens, activeNetwork, addTokenToNetwork, updateNetwork } =
    useDevMode()

  const navigate = useNavigate()

  useEffect(() => {
    if (!networkId) return

    setName(networks[networkId]?.name)
    setNewHostUrl(networks[networkId]?.hostUrl)
  }, [networks, networkId])

  const saveNetwork = () => {
    if (networkId) {
      updateNetwork(networkId, name!, newHostUrl!)
    } else {
      addNetwork(name!, newHostUrl!)
    }
    navigate(-1)
  }

  const renderPropertyRow = (kind: string, value?: string, onClick?: () => void) => (
    <ListRow
      primary={t(`newNetwork.${kind}.title`)}
      secondary={value || 'Not set'}
      chevron
      onClick={onClick}
    />
  )

  const renderTestTokens = () => {
    if (!networkId) return

    const tokens = networkTokens[networkId] || []

    return (
      <div className="mt-4 flex flex-col gap-2">
        <Heading level={5}>Network Test Tokens</Heading>
        {tokens.length > 0 && (
          <ListGroup>
            {tokens.map((networkToken, index) => (
              <ListRow
                key={index}
                leading={<TokenAvatar logo={networkToken.logo} />}
                primary={networkToken.name}
                secondary={networkToken.symbol}
              />
            ))}
          </ListGroup>
        )}
        <Button
          variant="link"
          onClick={() => setBottomSheetVisible(true)}
          disabled={networkId !== activeNetwork}
          fullWidth
        >
          {networkId !== activeNetwork
            ? 'Network should be activated to add tokens'
            : 'Add test token'}
        </Button>
        <BottomSheet
          visible={bottomSheetVisible}
          onBackdropClick={() => setBottomSheetVisible(false)}
        >
          <CustomTokenForm
            hideForm={() => setBottomSheetVisible(false)}
            customSaveAction={(tokenDetails) => addTokenToNetwork(networkId, tokenDetails)}
          />
        </BottomSheet>
      </div>
    )
  }

  return (
    <div className="mb-3 flex flex-col gap-4">
      <ListGroup>
        {renderPropertyRow('name', name, () => setKindInEdit('name'))}
        {renderPropertyRow('host', newHostUrl, () => setKindInEdit('host'))}
      </ListGroup>
      <Dialog
        open={!!kindInEdit}
        onClose={() => setKindInEdit(undefined)}
        title={t(`newNetwork.${kindInEdit}.title`)}
        actions={
          <Button fullWidth onClick={() => setKindInEdit(undefined)} variant="primary">
            {t('common.save')}
          </Button>
        }
      >
        <Input
          placeholder={t(`newNetwork.${kindInEdit}.placeholder`)}
          value={kindInEdit === 'name' ? name : newHostUrl}
          onChange={(event) =>
            kindInEdit === 'name' ? setName(event.target.value) : setNewHostUrl(event.target.value)
          }
          className="text-left"
          autoFocus
        />
      </Dialog>
      <Button
        onClick={() => saveNetwork()}
        variant="primary"
        disabled={!name || !newHostUrl}
        fullWidth
      >
        {t(networkId ? 'newNetwork.update' : 'newNetwork.create')}
      </Button>
      {renderTestTokens()}
    </div>
  )
}

export default NetworkForm
